<template>
  <tab-item-template>
    <template #toolbar-items>
      <v-spacer></v-spacer>
      <v-text-field
          v-model="filterText"
          clearable
          outlined
          dense
          placeholder="検索"
          light
          background-color="white"
          style="max-width: 300px;"
          hide-details>
      </v-text-field>
    </template>

    <el-table
        :data="dataProfileViewFiltered"
        border>
      <el-table-column
          label="名称"
          prop="name"
          width="300"
          sortable>
      </el-table-column>
      <el-table-column
          label="説明"
          prop="description"
          sortable>
        <template #default="{row}">
          <table-inline>
            <text-field-like
              :value="row.description"
              placeholder="未設定"
              multi-line>
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>
      <el-table-column
          width="200"
          label="共通辞書"
          prop="dictionaryName"
          sortable>
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.dictionaryName"
                :value-hds-id="row.dictionaryId">
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>
      <el-table-column
          width="200"
          label="コントローラ"
          prop="controller"
          sortable>
      </el-table-column>
      <el-table-column
          label="公開"
          prop="publishing"
          align="center"
          width="70">
        <template #default="{row}">
          <table-inline>
            <el-checkbox
                :value="row.dataImplementation && row.dataImplementation.publishing"
                @change="handlePublishingChange(row, $event)">
            </el-checkbox>
          </table-inline>
        </template>
      </el-table-column>
      <el-table-column
          width="200"
          align="center"
          fixed="right"
          column-key="operations"
          class-name="operations">
        <template #default="{row}">
          <v-btn
              v-if="row.produceId"
              depressed
              color="blue-grey white--text"
              :to="{name: nextPageName, params: {dataProfileId: row.produceId}}">
            <v-icon>el-icon-view</v-icon>
            提供
          </v-btn>
          <v-btn
              v-if="row.consumeId"
              depressed
              color="blue-grey darken-1 white--text"
              :to="{name: nextPageName, params: {dataProfileId: row.consumeId}}">
            <v-icon>el-icon-view</v-icon>
            利用
          </v-btn>
        </template>
      </el-table-column>
    </el-table>
  </tab-item-template>
</template>

<script>
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import {mapGetters, mapState} from "vuex";
import TableInline from "@/components/atoms/TableInline.vue";
import {SERVICE_TYPES} from "@/constants";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import Swal from "sweetalert2";
import groupArray from "group-array";

export default {
  name: "DataProfileTab",
  components: {TextFieldLike, TableInline, TabItemTemplate},
  props: {
    controllers: {
      type: Array,
      required: true,
    },
    dataProfiles: {
      type: Array,
      required: true,
    },
    dataComponentModels: {
      type: Array,
      required: true,
    },
    nextPageName: {
      type: String,
      default: '/profileManagement/dataProfile',
    },
  },
  data() {
    return {
      serviceTypes: SERVICE_TYPES,
      filterText: '',
    };
  },
  computed: {
    ...mapGetters({
      hcsClient: 'hcs/client',
    }),
    dataProfileView() {
      return Object.values(groupArray(this.dataProfiles, dp => `${dp.dataImplementationId} ${dp.dictionaryId} ${dp.dataComponentModelId}`)).map(group => {
        const first = group[0];
        if(first === undefined) {
          return undefined;
        }
        const di = first.dataImplementation;
        const controller = this.controllers.find(c => c.id === di?.controllerId);
        const commonDcm = this.dataComponentModels.find(d => d.id === first.dataComponentModelId);
        const localDcm = this.dataComponentModels.find(d => d.id === di?.dataComponentModelId);
        const consumeDataProfile = group.find(dp => dp.serviceType === 'consume');
        const produceDataProfile = group.find(dp => dp.serviceType === 'produce');
        return {
          ...first,
          name: localDcm?.name,
          description: localDcm?.description,
          dictionaryId: commonDcm?.dictionary?.id,
          dictionaryName: commonDcm?.dictionary?.name,
          dataImplementation: di,
          controller: controller?.name,
          consumeId: consumeDataProfile?.readProcessOperationImplementationsCount > 0 ? consumeDataProfile.id : null,
          produceId: produceDataProfile?.createProcessOperationImplementationsCount > 0 ? produceDataProfile.id : null,
          serviceType: undefined,
          id: undefined,
        }
      }).filter(dpv => dpv.consumeId || dpv.produceId);
    },
    dataProfileViewFiltered() {
      const f = this.filterText;
      if(f) {
        return this.dataProfileView.filter(dp =>
            dp.name.includes(f) ||
            dp.description?.includes(f) ||
            dp.dictionaryName.includes(f) ||
            dp.controller.includes(f)
        );
      } else {
        return this.dataProfileView;
      }
    }
  },
  methods: {
    async handlePublishingChange(dataProfile) {
      const isPublishing = !dataProfile.dataImplementation.publishing;
      const {title, html} = (() => {
        if(isPublishing) {
          return {
            title: 'データプロファイルの公開',
            html: `データプロファイルに関連するデータ実装「${dataProfile.name}」を公開します。よろしいですか？`
          };
        } else {
          return {
            title: 'データプロファイルの非公開',
            html: `データプロファイルに関連するデータ実装「${dataProfile.name}」を非公開にします。よろしいですか？`
          };
        }
      })();

      const {value: confirm} = await Swal.fire({
        title,
        html,
        icon: 'info',
        showCloseButton: true,
        showCancelButton: true,
        focusConfirm: true,
        confirmButtonText: "はい",
        cancelButtonText: "いいえ"
      });
      if(!confirm) {
        return;
      }

      dataProfile.dataImplementation.publishing = isPublishing;

      await this.hcsClient.patch(`/data_implementations/${dataProfile.dataImplementation.id}`, {
        publishing: isPublishing
      });
    }
  },
}
</script>

<style lang="scss" scoped src="./Operations.scss">
</style>
